/**
 * ChurchLivePage — public live-stream landing for a single church.
 *
 * Route: /churches/:slug/live
 * Source: GET /api/church/public/by-slug/{slug}/live
 *
 * Shows the live stream (YouTube iframe or custom embed/HLS) when active,
 * a countdown for the next scheduled stream, or a 'no upcoming streams'
 * empty state. Multi-platform watch buttons (FB/TikTok/Twitch/Restream)
 * are surfaced so visitors can pick where to watch.
 *
 * RTMP credentials are NEVER sent to this page — backend strips them.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useChurchViewer } from '@/lib/churchLiveRtc';
import { LiveStream } from '@/components/live/LiveStream';
import { BroadcastInfoPanel } from '@/components/live/BroadcastInfoPanel';
import { Badge } from '@/components/ui/badge';
import {
  Radio, Calendar, ArrowLeft, Loader2, Church, ExternalLink,
  Facebook, Youtube, Music2, Twitch, PlayCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtCountdown(target) {
  if (!target) return '';
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return 'Starting soon…';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `Starts in ${days}d ${hours}h`;
  if (hours > 0) return `Starts in ${hours}h ${mins}m`;
  return `Starts in ${mins}m`;
}

/** A phone broadcasting straight to this page. Receive-only. */
function PhoneStream({ churchId, title }) {
  const viewer = useChurchViewer(churchId, true);
  return (
    <div className="aspect-video w-full bg-black rounded-xl overflow-hidden shadow-2xl relative"
         data-testid="stream-webrtc">
      <video ref={viewer.attach} autoPlay playsInline controls
             className="w-full h-full object-contain" title={title} />
      {viewer.state !== 'playing' && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 p-6">
          <div className="text-center max-w-sm">
            <Radio className={`h-10 w-10 mx-auto mb-3 ${
              viewer.state === 'connecting' ? 'text-rose-500 animate-pulse' : 'text-slate-600'}`} />
            <p className="text-slate-200 text-base" data-testid="webrtc-state">
              {viewer.state === 'connecting' && 'Connecting to the service…'}
              {viewer.state === 'waiting' && 'Waiting for the broadcast to start…'}
              {viewer.state === 'offline' && 'The broadcast has ended.'}
              {viewer.state === 'stalled' && 'Cannot reach the broadcast.'}
              {viewer.state === 'failed' && 'Could not connect to the broadcast.'}
            </p>
            {viewer.message && ['failed', 'stalled'].includes(viewer.state) && (
              <p className="text-slate-500 text-sm mt-1.5">{viewer.message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** The same service, relayed by the church's LiveKit server. Receive-only. */
function LiveKitStream({ churchId, title }) {
  return <LiveStream scope="church" ident={churchId} title={title} />;
}

function StreamPlayer({ stream }) {
  // iter 645 — the backend resolves which source is actually playable and
  // rewrites it into an embeddable form (a youtube /watch link cannot be
  // framed; a Facebook video has to go through the plugin). Older streams have
  // no `player` key until the API answers, so the legacy fields stay as a
  // fallback rather than showing an empty box in the meantime.
  const player = stream.player;
  const [refused, setRefused] = useState(false);

  useEffect(() => { setRefused(false); }, [player?.src]);

  const frame = (src, testid) => (
    <div className="aspect-video w-full bg-black rounded-xl overflow-hidden shadow-2xl">
      <iframe
        title={stream.title}
        src={src}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        data-testid={testid}
      />
    </div>
  );

  if (player && player.kind === 'livekit') {
    return <LiveKitStream churchId={stream.church_id} title={stream.title} />;
  }
  if (player && player.kind === 'webrtc') {
    return <PhoneStream churchId={stream.church_id} title={stream.title} />;
  }
  if (player && player.kind === 'iframe' && !refused) {
    // Twitch refuses to play unless `parent` matches the page framing it, and
    // only the browser knows the public hostname.
    const src = player.needs_parent
      ? `${player.src}&parent=${window.location.hostname}`
      : player.src;
    return frame(src, 'stream-iframe-embed');
  }
  if (player && player.kind === 'video' && !refused) {
    return (
      <div className="aspect-video w-full bg-black rounded-xl overflow-hidden shadow-2xl flex items-center justify-center" data-testid="stream-hls">
        <video src={player.src} controls autoPlay muted className="w-full h-full"
               onError={() => setRefused(true)} />
      </div>
    );
  }
  if (!player) {
    if (stream.youtube_video_id) {
      return frame(`https://www.youtube.com/embed/${stream.youtube_video_id}?autoplay=1&rel=0`, 'stream-iframe-yt');
    }
    if (stream.embed_url) return frame(stream.embed_url, 'stream-iframe-embed');
  }

  // Nothing playable. The old copy here read "No embed configured", which told
  // a viewer nothing and an admin no more. Say where else to watch, and why.
  // `refused` means a source WAS saved and the browser could not play it —
  // saying "no watch link has been added" in that case is simply wrong.
  const elsewhere = (player?.alternates || []).filter((a) => a.url);
  if (refused) {
    const here = player?.src && player.kind === 'video' ? player.src : '';
    return (
      <div className="aspect-video w-full bg-slate-900 rounded-xl flex items-center justify-center p-6" data-testid="stream-no-embed">
        <div className="text-center max-w-md">
          <Radio className="h-10 w-10 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-200 text-base mb-1">This stream would not load.</p>
          <p className="text-slate-500 text-sm mb-4">
            It may not have started yet, or your browser cannot play this format.
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {here && (
              <a href={here} target="_blank" rel="noopener noreferrer" data-testid="stream-open-direct">
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-200">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Open the stream
                </Button>
              </a>
            )}
            {elsewhere.map((a) => (
              <a key={a.url} href={a.url} target="_blank" rel="noopener noreferrer"
                 data-testid={`stream-fallback-watch-${a.platform}`}>
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-200">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Watch on {a.label || a.platform}
                </Button>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="aspect-video w-full bg-slate-900 rounded-xl flex items-center justify-center p-6" data-testid="stream-no-embed">
      <div className="text-center max-w-md">
        <Radio className="h-10 w-10 mx-auto mb-3 text-slate-600" />
        {elsewhere.length > 0 ? (
          <>
            <p className="text-slate-200 text-base mb-1">This service is streaming on another platform.</p>
            <p className="text-slate-500 text-sm mb-4">It cannot be played inside this page.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {elsewhere.map((a) => (
                <a key={a.url} href={a.url} target="_blank" rel="noopener noreferrer"
                   data-testid={`stream-fallback-watch-${a.platform}`}>
                  <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-200">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Watch on {a.label || a.platform}
                  </Button>
                </a>
              ))}
            </div>
          </>
        ) : (
          <p className="text-slate-400 text-sm" data-testid="stream-no-embed-reason">
            {player?.reason || 'No watch link has been added to this stream yet.'}
          </p>
        )}
      </div>
    </div>
  );
}

function PlatformLinks({ stream }) {
  const links = [
    { key: 'youtube',  label: 'Watch on YouTube',  href: stream.youtube_video_id ? `https://youtube.com/watch?v=${stream.youtube_video_id}` : '', icon: Youtube,   tone: 'bg-red-600 hover:bg-red-700' },
    { key: 'facebook', label: 'Watch on Facebook', href: stream.facebook_url, icon: Facebook, tone: 'bg-blue-600 hover:bg-blue-700' },
    { key: 'tiktok',   label: 'Watch on TikTok',   href: stream.tiktok_url,   icon: Music2,   tone: 'bg-slate-900 hover:bg-black' },
    { key: 'twitch',   label: 'Watch on Twitch',   href: stream.twitch_url,   icon: Twitch,   tone: 'bg-purple-600 hover:bg-purple-700' },
    { key: 'restream', label: 'Watch on Restream', href: stream.restream_url, icon: PlayCircle, tone: 'bg-emerald-600 hover:bg-emerald-700' },
  ].filter(l => l.href);
  if (links.length === 0) return null;
  return (
    <div className="flex flex-wrap justify-center gap-2 mt-4" data-testid="stream-platform-links">
      {links.map(l => (
        <a key={l.key} href={l.href} target="_blank" rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-2 rounded-lg transition-colors ${l.tone}`}
          data-testid={`stream-watch-${l.key}`}>
          <l.icon className="h-3.5 w-3.5" />{l.label}<ExternalLink className="h-3 w-3 opacity-70" />
        </a>
      ))}
    </div>
  );
}

const ChurchLivePage = () => {
  const { slug } = useParams();
  const [tick, setTick] = useState(0);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-live', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/church/public/by-slug/${slug}/live`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    enabled: !!slug,
    refetchInterval: 30_000,    // re-check every 30s so 'go-live' is reflected quickly
    retry: false,
  });

  // Tick every 30s for countdown re-render
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  void tick;  // suppress unused warning

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (isError || !data?.church) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 text-white">
        <Card className="max-w-md w-full bg-slate-900 border-slate-800"><CardContent className="py-10 text-center text-slate-300">
          <Church className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-lg font-bold">Church not found</p>
          <p className="text-sm text-slate-500 mt-2">The link may be inactive or the slug is wrong.</p>
          <Link to="/" className="text-emerald-400 text-sm mt-4 inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-3 w-3" />Back home</Link>
        </CardContent></Card>
      </div>
    );
  }

  const { church, live, next, last } = data;
  const active = live || next;
  // iter 648 — "LIVE NOW" means a congregation can actually watch something.
  // A stream whose status is live but which has no playable source (nothing
  // pasted, or only an rtmp:// ingress address, or a phone whose connection
  // has dropped) is announced as starting shortly instead. The badge used to
  // follow the status flag alone, which is how a church advertised a service
  // above an empty player.
  const watchable = !!live && live.player?.kind !== 'none';

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="church-live-page">
      <header className="border-b border-slate-800/60">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-3 flex-wrap">
          <Link to={`/churches/${church.slug}`} className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />{church.name}
          </Link>
          {watchable ? (
            <Badge variant="outline" className="text-rose-300 border-rose-500/50 bg-rose-500/10 animate-pulse" data-testid="stream-live-badge">
              <Radio className="h-3 w-3 mr-1" />LIVE NOW
            </Badge>
          ) : live ? (
            <Badge variant="outline" className="text-amber-300 border-amber-500/50 bg-amber-500/10" data-testid="stream-starting-badge">
              <Radio className="h-3 w-3 mr-1" />Starting shortly
            </Badge>
          ) : null}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {active ? (
          <>
            <div className="mb-4">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-400">
                {watchable ? 'Live now' : live ? 'Starting shortly' : 'Up next'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold mt-1">{active.title}</h1>
              {!live && active.scheduled_at && (
                <p className="text-sm text-slate-400 mt-1 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(active.scheduled_at).toLocaleString()} · <span className="text-amber-400 font-medium">{fmtCountdown(active.scheduled_at)}</span>
                </p>
              )}
            </div>

            {live ? <StreamPlayer stream={active} /> : (
              <Card className="bg-slate-900 border-slate-800 aspect-video flex items-center justify-center text-center">
                <CardContent className="p-8">
                  <Calendar className="h-12 w-12 text-emerald-400 mx-auto mb-3 opacity-60" />
                  <p className="text-lg font-bold">{fmtCountdown(active.scheduled_at)}</p>
                  <p className="text-sm text-slate-400 mt-2">Come back at the scheduled time to watch live.</p>
                </CardContent>
              </Card>
            )}

            {active.player?.kind !== 'none' && <PlatformLinks stream={active} />}

            {active.description && (
              <Card className="bg-slate-900 border-slate-800 mt-6"><CardContent className="p-4">
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{active.description}</p>
              </CardContent></Card>
            )}

            <BroadcastInfoPanel scope="church" ident={church.id} tone="dark" live={live} />
          </>
        ) : (
          <Card className="bg-slate-900 border-slate-800" data-testid="stream-empty">
            <CardContent className="py-16 text-center">
              <Radio className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-xl font-bold text-slate-200">No upcoming streams</p>
              <p className="text-sm text-slate-500 mt-2">Check back soon — services and special events will appear here.</p>
              <Link to={`/churches/${church.slug}`} className="text-emerald-400 text-sm mt-4 inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-3 w-3" />Back to {church.name}</Link>
            </CardContent>
          </Card>
        )}

        {last && !live && (
          <Card className="bg-slate-900 border-slate-800 mt-6"><CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-200 flex items-center gap-2"><PlayCircle className="h-4 w-4 text-emerald-400" />Last broadcast</CardTitle>
          </CardHeader><CardContent>
            <p className="text-sm text-slate-300">{last.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{(last.ended_at || last.scheduled_at)?.replace('T', ' ').slice(0, 16)}</p>
            <PlatformLinks stream={last} />
          </CardContent></Card>
        )}
      </main>

      <footer className="text-center text-xs text-slate-600 py-6">© {new Date().getFullYear()} {church.name}.</footer>
    </div>
  );
};

export default ChurchLivePage;
